import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TextField,
  IconButton,
} from "@mui/material";
import { Edit, Save, Delete } from "@mui/icons-material";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import { showErrorSnackbar } from "../../../../redux/slices/snackbar";
import { useDialog } from "../../../../contexts/DialogContext";
import DeleteOttPlanModal from "./DeleteOttPlanModal";

const InternetPlanTable = () => {
  const { openDialog } = useDialog();
  const dispatch = useDispatch();
  const { watch, setValue } = useFormContext();

  const planGroupOttPlans = watch("planGroupOttPlans") || [];
  const serviceType = watch("serviceType");
  const sharingType = watch("sharingType");

  const [editRowId, setEditRowId] = useState(null);
  const [editValues, setEditValues] = useState({});

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Handlers
  const handleEdit = (row) => {
    setEditRowId(row.ottPlanId);
    setEditValues(row);
  };

  const handleSave = () => {
    const totalOttSharing =
      Number(editValues.ottMsoSharing) +
      Number(editValues.ottDistSharing) +
      Number(editValues.ottLcoSharing);
    if (totalOttSharing != 100 && editValues.isSharingAllowed) {
      dispatch(showErrorSnackbar("OTT sharing should be equal to 100"));
      return;
    }
    const updatedPlans = planGroupOttPlans.map((plan) =>
      plan.OttPlanId === editRowId ? { ...editValues } : plan,
    );

    setValue("planGroupOttPlans", updatedPlans, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setEditRowId(null);
  };

  const handleChange = (e, field) => {
    setEditValues({ ...editValues, [field]: e.target.value });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // reset to first page
  };

  const handleDelete = (ottPlanId) => {
    return openDialog(
      "delete_ott_plan",
      "Delete sharing",
      <DeleteOttPlanModal
        ottPlanId={ottPlanId}
        setValue={setValue}
        planGroupOttPlans={planGroupOttPlans}
      />,
    );
  };

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>OTT Plan Name</strong>
              </TableCell>
              <TableCell>
                <strong>OTT MSO Sharing</strong>
              </TableCell>
              <TableCell>
                <strong>OTT Dist Sharing</strong>
              </TableCell>
              <TableCell>
                <strong>OTT LCO Sharing</strong>
              </TableCell>
              <TableCell>
                <strong>Action</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {planGroupOttPlans
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row) => (
                <TableRow key={row.ottPlanId}>
                  <TableCell>{row.ottPlanName}</TableCell>

                  <TableCell>
                    {editRowId === row.ottPlanId ? (
                      <TextField
                        value={editValues.ottMsoSharing}
                        onChange={(e) => handleChange(e, "ottMsoSharing")}
                        size="small"
                      />
                    ) : (
                      `${row.ottMsoSharing ? row.ottMsoSharing : 0}%`
                    )}
                  </TableCell>

                  <TableCell>
                    {editRowId === row.ottPlanId ? (
                      <TextField
                        value={editValues.ottDistSharing}
                        onChange={(e) => handleChange(e, "ottDistSharing")}
                        size="small"
                      />
                    ) : (
                      `${row.ottDistSharing ? row.ottDistSharing : 0}%`
                    )}
                  </TableCell>

                  <TableCell>
                    {editRowId === row.ottPlanId ? (
                      <TextField
                        value={editValues.ottLcoSharing}
                        onChange={(e) => handleChange(e, "ottLcoSharing")}
                        size="small"
                      />
                    ) : (
                      `${row.ottLcoSharing ? row.ottLcoSharing : 0}%`
                    )}
                  </TableCell>

                  <TableCell>
                    {editRowId === row.ottPlanId ? (
                      <>
                        <IconButton onClick={handleSave} color="primary">
                          <Save />
                        </IconButton>
                        <IconButton onClick={() => handleDelete(row.ottPlanId)} color="error">
                          <Delete />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        {sharingType !== "FIXED" && (
                          <IconButton onClick={() => handleEdit(row)} color="secondary">
                            <Edit />
                          </IconButton>
                        )}
                        <IconButton onClick={() => handleDelete(row.ottPlanId)} color="error">
                          <Delete />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[50, 100]}
        component="div"
        count={planGroupOttPlans.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default InternetPlanTable;
